import { useState, useRef } from 'react';
import { PaperClipIcon, XMarkIcon, DocumentIcon } from '@heroicons/react/24/outline';
import type { ClubTheme } from './themes';
import type { SocialChannel, SocialPost } from './types';
import { useCreatePost, useUploadAttachment } from './queries';
import type { MediaAttachment } from './queries';
import QuotedPostEmbed from './QuotedPostEmbed';

interface PostComposerProps {
  clubId: string;
  channels: SocialChannel[];
  theme: ClubTheme;
  quotedPost?: SocialPost | null;
  onClearQuote?: () => void;
  onPosted?: () => void;
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PostComposer({ clubId, channels, theme, quotedPost, onClearQuote, onPosted }: PostComposerProps) {
  const [body, setBody] = useState('');
  const [channelSlug, setChannelSlug] = useState<string>(channels[0]?.slug || '');
  const [attachments, setAttachments] = useState<MediaAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [circleOnly, setCircleOnly] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const createPost = useCreatePost(clubId);
  const upload = useUploadAttachment(clubId);

  const hasContent = body.trim() || attachments.length > 0;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be re-selected
    e.target.value = '';

    const ALLOWED_TYPES = new Set([
      'application/pdf',
      'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
      'audio/mpeg', 'audio/wav',
      'video/mp4', 'video/webm',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain', 'text/csv',
    ]);
    if (!ALLOWED_TYPES.has(file.type)) {
      alert('File type not supported. Allowed: images, PDFs, documents, audio, video.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File must be under 10 MB');
      return;
    }
    if (attachments.length >= 3) {
      alert('Maximum 3 attachments per post');
      return;
    }

    setUploading(true);
    try {
      const media = await upload.mutateAsync(file);
      setAttachments((prev) => [...prev, media]);
    } catch (err) {
      console.error('Upload failed', err);
      alert('Upload failed — please try again');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = () => {
    if (!hasContent || createPost.isPending) return;

    createPost.mutate(
      {
        body: body.trim(),
        channel: channelSlug || undefined,
        media_urls: attachments.length > 0 ? attachments : undefined,
        quoted_post_id: quotedPost?.id,
        metadata: circleOnly ? { circle_only: true } : undefined,
      },
      {
        onSuccess: () => {
          setBody('');
          setAttachments([]);
          setCircleOnly(false);
          onClearQuote?.();
          onPosted?.();
        },
      },
    );
  };

  return (
    <div
      className="rounded-2xl p-4"
      style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.muted}20` }}
    >
      {/* Quoted post preview */}
      {quotedPost && (
        <div className="relative mb-2">
          <QuotedPostEmbed
            post={{
              id: quotedPost.id,
              body: quotedPost.body,
              author: quotedPost.author,
              created_at: quotedPost.created_at,
            }}
            theme={theme}
          />
          <button
            type="button"
            onClick={onClearQuote}
            className="absolute right-2 top-2 rounded-full p-0.5 hover:opacity-70"
            style={{ color: theme.colors.muted, backgroundColor: `${theme.colors.surface}CC` }}
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={quotedPost ? 'Add your thoughts...' : `Share something with ${theme.name}...`}
        rows={3}
        className="w-full resize-none rounded-xl p-3 text-sm outline-none"
        style={{
          backgroundColor: `${theme.colors.muted}10`,
          color: theme.colors.text,
          border: `1px solid ${theme.colors.muted}20`,
        }}
      />

      {/* Attached files */}
      {attachments.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {attachments.map((att, i) => {
            const isImage = att.type.startsWith('image/');
            return (
              <div
                key={i}
                className="flex items-center gap-2 rounded-lg px-3 py-1.5"
                style={{ backgroundColor: `${theme.colors.muted}10`, border: `1px solid ${theme.colors.muted}15` }}
              >
                {isImage ? (
                  <img src={att.url} alt={att.alt || ''} className="h-8 w-8 rounded object-cover shrink-0" />
                ) : (
                  <DocumentIcon className="h-4 w-4 shrink-0" style={{ color: theme.colors.primary }} />
                )}
                <span className="flex-1 truncate text-xs" style={{ color: theme.colors.text }}>
                  {att.alt || 'Attachment'}
                </span>
                {att.size != null && (
                  <span className="text-[10px] shrink-0" style={{ color: theme.colors.muted }}>
                    {humanSize(att.size)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeAttachment(i)}
                  className="shrink-0 rounded p-0.5 hover:opacity-70"
                  style={{ color: theme.colors.muted }}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <select
            value={channelSlug}
            onChange={(e) => setChannelSlug(e.target.value)}
            className="rounded-lg px-2 py-1 text-xs outline-none"
            style={{
              backgroundColor: `${theme.colors.muted}15`,
              color: theme.colors.text,
              border: `1px solid ${theme.colors.muted}30`,
            }}
          >
            {channels.map((ch) => (
              <option key={ch.slug} value={ch.slug}>
                # {ch.name}
              </option>
            ))}
          </select>

          {/* My Circle toggle */}
          <button
            type="button"
            onClick={() => setCircleOnly(!circleOnly)}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium transition-colors"
            style={{
              backgroundColor: circleOnly ? `${theme.colors.primary}20` : `${theme.colors.muted}10`,
              color: circleOnly ? theme.colors.primary : theme.colors.muted,
              border: circleOnly ? `1px solid ${theme.colors.primary}40` : `1px solid transparent`,
            }}
            title="Only visible to people in your circle"
          >
            🔗 {circleOnly ? 'Circle' : 'Everyone'}
          </button>

          {/* Attach file */}
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.svg,.mp3,.wav,.mp4,.webm,.docx,.xlsx,.pptx,.txt,.csv"
            className="hidden"
            onChange={handleFileSelect}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || attachments.length >= 3}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs transition-opacity disabled:opacity-40"
            style={{ color: theme.colors.muted }}
            title="Attach file (images, PDFs, documents)"
          >
            <PaperClipIcon className="h-4 w-4" />
            {uploading && <span>Uploading...</span>}
          </button>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!hasContent || createPost.isPending || uploading}
          className="rounded-xl px-4 py-1.5 text-sm font-semibold transition-opacity disabled:opacity-40"
          style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
        >
          {createPost.isPending ? 'Posting...' : 'Post'}
        </button>
      </div>

      {createPost.error && (
        <p className="mt-2 text-xs text-red-400">{createPost.error.message}</p>
      )}
    </div>
  );
}
